/**
 * OffersLanding Component
 * Main offers page that orchestrates partner check, user details, and SDK launch
 */

import React, { useCallback, useEffect, useState } from 'react';

import { BalanceDisplay } from '../../components/balance-display';
import { LandingPageLayout } from '../../components/landing-page-layout';
import { Loader } from '../../components/loader';
import { PartnerDisabled } from '../../components/partner-disabled';
import { useAppDispatch, useAppSelector, usePartnerStatus, useSDKLauncher } from '../../hooks';
import { selectIsWidgetLoaded, selectUserBalance, setIsAnimating } from '../../store/slices';
import { SDKLauncher } from '../sdk-launcher';
import { UserDetailsForm } from '../user-details-form';

import './offers-landing.scss';

export const OffersLanding: React.FC = () => {
    const dispatch = useAppDispatch();
    const {
        isPartnerLive,
        isLoading,
        hasValidEmail,
        showUserDetails,
        partnerDisabledData,
        error: partnerError,
    } = usePartnerStatus();

    const balance = useAppSelector(selectUserBalance);
    const isWidgetLoaded = useAppSelector(selectIsWidgetLoaded);
    const { isLoading: sdkLoading, error: sdkError } = useSDKLauncher();
    const [hasUserDefinedEmail, setHasUserDefinedEmail] = useState(false);

    // Animation effect on mount
    useEffect(() => {
        const timer = setTimeout(() => {
            dispatch(setIsAnimating(true));
        }, 500);

        return () => {
            clearTimeout(timer);
        };
    }, [dispatch]);

    // Handle user details form submission
    const handleUserDetailsSuccess = useCallback(() => {
        setHasUserDefinedEmail(true);
    }, []);

    // Determine what state we're in - only show content when we have a DEFINITIVE state
    // Definitive states are:
    // 1. Error (partnerError or sdkError)
    // 2. Partner disabled
    // 3. Need user details (showUserDetails && !hasValidEmail)
    // 4. SDK is loaded (isWidgetLoaded)
    // 5. No valid email and no user details form (need email form - but this shouldn't happen in normal flow)

    const hasDefinitiveError = !!partnerError || !!sdkError;
    const isPartnerDisabled = !isPartnerLive && !!partnerDisabledData;
    const needsUserDetails = showUserDetails && !hasValidEmail;
    const sdkIsReady = isWidgetLoaded;

    // We have a definitive state if any of these are true
    const hasDefinitiveState = hasDefinitiveError || isPartnerDisabled || needsUserDetails || sdkIsReady;

    // Show loader if:
    // 1. Partner check is in progress, OR
    // 2. SDK is loading (prevents header flash during SDK initialization), OR
    // 3. We don't have a definitive state yet (still waiting for checks/SDK)
    const shouldShowLoader = isLoading || sdkLoading || !hasDefinitiveState;

    // Show fullscreen loader while we don't have a definitive state
    // ALSO render SDKLauncher (hidden) if we have valid email so it can initialize
    if (shouldShowLoader) {
        return (
            <>
                <Loader fullPage pulse />
                {/* If user has valid email, render SDKLauncher hidden so it can trigger SDK initialization */}
                {hasValidEmail && !sdkError && (
                    <div style={{ display: 'none' }}>
                        <SDKLauncher autoLaunch={true} />
                    </div>
                )}
            </>
        );
    }

    // API Error state - show error message instead of partner disabled
    if (partnerError) {
        return (
            <LandingPageLayout className="offers-landing offers-landing--error">
                <div className="offers-landing__error">
                    <h2>Unable to load offers</h2>
                    <p>{partnerError}</p>
                    <p>Please try refreshing the page or logging in again.</p>
                </div>
            </LandingPageLayout>
        );
    }

    // Partner disabled state (only if we have valid disabled data, not an error)
    if (!isPartnerLive && partnerDisabledData && !partnerError) {
        return <PartnerDisabled data={partnerDisabledData} />;
    }

    // Show user details form (only if we need user details)
    if (showUserDetails && !hasValidEmail) {
        return (
            <LandingPageLayout className="offers-landing offers-landing--user-details">
                <BalanceDisplay balance={balance / 100} size="medium" />
                <UserDetailsForm onSuccess={handleUserDetailsSuccess} />
            </LandingPageLayout>
        );
    }

    // SDK Error state - show error with retry option
    if (sdkError) {
        return (
            <LandingPageLayout className="offers-landing offers-landing--error">
                <BalanceDisplay balance={balance / 100} size="medium" />
                <SDKLauncher autoLaunch={false} />
            </LandingPageLayout>
        );
    }

    // If SDK is loaded, don't render anything - SDK has taken over the screen
    if (isWidgetLoaded) {
        return null;
    }

    // Main offers landing (email confirmation / SDK launch)
    // This should only show if user doesn't have valid email (needs email form)
    return (
        <LandingPageLayout className="offers-landing">
            <BalanceDisplay balance={balance / 100} showConfirmed={hasUserDefinedEmail} />
            <SDKLauncher autoLaunch={hasValidEmail} />
        </LandingPageLayout>
    );
};

export default OffersLanding;
